import React, { useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import {
  useCopyProductAnnouncementMutation,
  useCreateProductAnnouncementMutation,
  useDeleteProductAnnouncementMediaMutation,
  useDeleteProductAnnouncementMutation,
  useProductAnnouncementQuery,
  useProductAnnouncementsQuery,
  useUpdateProductAnnouncementMutation,
  useUploadProductAnnouncementMediaMutation,
} from "apis/productAnnouncement";
import AnnouncementActionsMenu from "components/productAnnouncement/AnnouncementActionsMenu";
import AnnouncementEditor from "components/productAnnouncement/AnnouncementEditor";
import AnnouncementPreview from "components/productAnnouncement/AnnouncementPreview";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import PageLoader from "components/ui/PageLoader";
import Table from "components/ui/Table";
import { showToast } from "components/ui/toast";
import {
  PRODUCT_ANNOUNCEMENT_TARGETS,
  PRODUCT_ANNOUNCEMENT_STATUS,
  formatAnnouncementDate,
  getApiErrorMessage,
} from "utils/productAnnouncement";

const PAGE_SIZE = 25;
const actionButtonClass =
  "rounded-md border border-mediumgray px-2 py-1 text-xs font-medium text-darkergray hover:bg-lightgray disabled:cursor-not-allowed disabled:opacity-40";

const ProductAnnouncements = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [publicationState, setPublicationState] = useState("");
  const [target, setTarget] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isEditorDirty, setIsEditorDirty] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      ...(search && { search }),
      ...(publicationState && { publicationState }),
      ...(target && { target }),
    }),
    [page, publicationState, search, target],
  );

  const { data, isPending, isFetching, isError, error: listError } =
    useProductAnnouncementsQuery(queryParams);
  const {
    data: selectedAnnouncement,
    isPending: isSelectedPending,
    isError: isSelectedError,
    error: selectedError,
  } = useProductAnnouncementQuery(selectedId);
  const createMutation = useCreateProductAnnouncementMutation();
  const updateMutation = useUpdateProductAnnouncementMutation();
  const deleteMutation = useDeleteProductAnnouncementMutation();
  const copyMutation = useCopyProductAnnouncementMutation();
  const uploadMediaMutation = useUploadProductAnnouncementMediaMutation();
  const deleteMediaMutation = useDeleteProductAnnouncementMediaMutation();

  const announcements = data?.announcements || [];
  const pagination = data?.pagination || {
    page: 1,
    pages: 1,
    total: 0,
    limit: PAGE_SIZE,
  };
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isMediaBusy =
    uploadMediaMutation.isPending || deleteMediaMutation.isPending;

  const openEditor = (announcement = null) => {
    setSelectedId(announcement?._id || null);
    setIsEditorDirty(false);
    setIsEditorOpen(true);
  };

  const discardEditor = () => {
    setIsDiscardConfirmOpen(false);
    setIsEditorDirty(false);
    setIsEditorOpen(false);
  };

  const closeEditor = () => {
    if (isEditorDirty) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    discardEditor();
  };

  const openPreview = (announcement) => {
    setSelectedId(announcement._id);
    setIsPreviewOpen(true);
  };

  const saveAnnouncement = async (payload, stagedMedia) => {
    if (!selectedId) {
      const created = await createMutation.mutateAsync({
        announcement: payload,
        stagedMedia,
      });
      setSelectedId(created._id);
      showToast("success", "Draft created.");
      return created;
    }
    const updated = await updateMutation.mutateAsync({
      id: selectedId,
      ...payload,
    });
    showToast("success", "Announcement saved.");
    return updated;
  };

  const publishAnnouncement = async (payload, stagedMedia) => {
    let id = selectedId;
    let createdDraft = false;
    if (!id) {
      const created = await createMutation.mutateAsync({
        announcement: payload,
        stagedMedia,
      });
      id = created._id;
      createdDraft = true;
    }
    try {
      await updateMutation.mutateAsync({
        id,
        ...payload,
        publicationState: "published",
      });
    } catch (error) {
      if (createdDraft) {
        setSelectedId(id);
        showToast(
          "error",
          "The draft was saved, but publication failed. Review the message and try again.",
        );
      }
      throw error;
    }
    showToast(
      "success",
      "Announcement published for its selected recipients.",
    );
    setIsEditorDirty(false);
    setIsEditorOpen(false);
  };

  const notifyAgain = async (payload) => {
    await updateMutation.mutateAsync({
      id: selectedId,
      ...payload,
      notifyAgain: true,
    });
    showToast("success", "A new delivery version has been created.");
    setIsEditorDirty(false);
    setIsEditorOpen(false);
  };

  const uploadMedia = async (file, altText, caption) => {
    const asset = await uploadMediaMutation.mutateAsync({
      id: selectedId,
      file,
      altText,
      caption,
    });
    showToast("success", "Media uploaded.");
    return asset;
  };

  const deleteMedia = async (mediaId) => {
    await deleteMediaMutation.mutateAsync({ id: selectedId, mediaId });
    showToast("success", "Media removed.");
  };

  const duplicateAnnouncement = async (announcement) => {
    if (copyMutation.isPending) return;
    try {
      const copy = await copyMutation.mutateAsync(announcement._id);
      showToast("success", "Draft copy created.");
      openEditor(copy);
    } catch (error) {
      showToast(
        "error",
        getApiErrorMessage(error, "Could not duplicate the announcement."),
      );
    }
  };

  const confirmPendingAction = async () => {
    const { type, announcement } = pendingAction;
    try {
      if (type === "archive") {
        await updateMutation.mutateAsync({
          id: announcement._id,
          publicationState: "archived",
        });
        showToast("success", "Announcement archived.");
      }
      if (type === "delete") {
        await deleteMutation.mutateAsync(announcement._id);
        showToast("success", "Draft deleted.");
      }
      setPendingAction(null);
    } catch (error) {
      showToast(
        "error",
        getApiErrorMessage(error, `Could not ${type} the announcement.`),
      );
    }
  };

  const columns = [
      {
        accessorKey: "name",
        header: "Announcement",
        cell: (row) => (
          <div className="min-w-56">
            <div className="font-medium text-darkergray">{row.name}</div>
            <div className="mt-1 truncate text-xs text-darkgray" title={row.title}>
              {row.title || "No recipient headline yet"}
            </div>
          </div>
        ),
        isSortable: false,
        isFilter: false,
      },
      {
        accessorKey: "displayStatus",
        header: "Status",
        cell: (row) => {
          const status =
            PRODUCT_ANNOUNCEMENT_STATUS[row.displayStatus] ||
            PRODUCT_ANNOUNCEMENT_STATUS.draft;
          return (
            <div className="space-y-1">
              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${status.className}`}>
                {status.label}
              </span>
              {row.deliveryVersion > 0 && (
                <div className="text-xs text-darkgray">Version {row.deliveryVersion}</div>
              )}
            </div>
          );
        },
        isSortable: false,
        isFilter: false,
      },
      {
        accessorKey: "audience.targets",
        header: "Audience",
        cell: (row) => (
          <div className="max-w-xs">
            <div className="flex flex-wrap gap-1">
              {(row.audience?.targets || []).length ? (
                row.audience.targets.map((value) => (
                  <span key={value} className="rounded bg-blue-50 px-2 py-1 text-xs text-blue-800">
                    {PRODUCT_ANNOUNCEMENT_TARGETS.find((item) => item.value === value)?.label || value}
                  </span>
                ))
              ) : (
                <span className="text-xs text-darkgray">Not selected</span>
              )}
            </div>
            <div className="mt-1 text-xs text-darkgray">
              {row.audience?.scope === "organizations"
                ? `${row.audience.organizationIds?.length || 0} selected organization(s)`
                : row.audience?.scope === "accounts"
                  ? `${row.audience.accounts?.length || 0} selected person/people`
                  : "Everyone in selected roles"}
            </div>
          </div>
        ),
        isSortable: false,
        isFilter: false,
      },
      {
        accessorKey: "startsAt",
        header: "Schedule",
        cell: (row) => (
          <div className="whitespace-nowrap text-xs">
            <div>
              {row.startsAt
                ? formatAnnouncementDate(row.startsAt)
                : "On publish"}
            </div>
            {row.endsAt && <div className="mt-1 text-darkgray">to {formatAnnouncementDate(row.endsAt)}</div>}
          </div>
        ),
        isSortable: false,
        isFilter: false,
      },
      {
        accessorKey: "updatedBy.name",
        header: "Last edited",
        cell: (row) => (
          <div className="text-xs">
            <div>{row.updatedBy?.name || row.updatedBy?.email || "Admin"}</div>
            <div className="mt-1 text-darkgray">{formatAnnouncementDate(row.updatedAt)}</div>
          </div>
        ),
        isSortable: false,
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => {
          const menuItems = [
            row.publicationState === "published" && {
              label: "Archive",
              isDestructive: true,
              onSelect: () =>
                setPendingAction({ type: "archive", announcement: row }),
            },
            row.publicationState === "draft" && {
              label: "Delete draft",
              isDestructive: true,
              onSelect: () =>
                setPendingAction({ type: "delete", announcement: row }),
            },
          ].filter(Boolean);
          return (
            <div className="flex items-center gap-2">
              {row.publicationState !== "archived" && (
                <button type="button" onClick={(event) => { event.stopPropagation(); openEditor(row); }} className={actionButtonClass}>
                  Edit
                </button>
              )}
              <button type="button" onClick={(event) => { event.stopPropagation(); duplicateAnnouncement(row); }} className={actionButtonClass} disabled={copyMutation.isPending}>
                Duplicate
              </button>
              {!!menuItems.length && <AnnouncementActionsMenu items={menuItems} />}
            </div>
          );
        },
        isSortable: false,
        isFilter: false,
        stopPropagation: true,
      },
  ];

  const CreateButton = () => (
    <Button onClick={() => openEditor()} className="black-button flex items-center gap-2">
      <FiPlus size={16} /> New announcement
    </Button>
  );

  if (isPending) return <PageLoader />;

  return (
    <div className="container mx-auto p-4">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-darkergray">Product announcements</h1>
        <p className="mt-1 text-sm text-darkgray">
          Create and control “What&apos;s new in Beleef” content for App audiences. Click a row to preview it.
        </p>
      </div>

      {isError && (
        <div role="alert" className="mb-5 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {getApiErrorMessage(listError, "Could not load product announcements.")}
        </div>
      )}

      <div className="mb-5 rounded-lg border border-mediumgray bg-white p-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-darkergray" htmlFor="announcement-state-filter">Publication state</label>
            <select id="announcement-state-filter" value={publicationState} onChange={(event) => { setPublicationState(event.target.value); setPage(1); }} className="w-full rounded-md border border-mediumgray px-3 py-2 text-sm">
              <option value="">All states</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-darkergray" htmlFor="announcement-target-filter">Audience</label>
            <select id="announcement-target-filter" value={target} onChange={(event) => { setTarget(event.target.value); setPage(1); }} className="w-full rounded-md border border-mediumgray px-3 py-2 text-sm">
              <option value="">All audiences</option>
              {PRODUCT_ANNOUNCEMENT_TARGETS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={() => { setPublicationState(""); setTarget(""); setPage(1); }} className="gray-button w-full">
              Clear audience filters
            </Button>
          </div>
        </div>
      </div>

      {isFetching && <div className="mb-2 text-xs text-darkgray">Refreshing announcements…</div>}
      <Table
        title="Announcements"
        columns={columns}
        data={announcements}
        pageSize={PAGE_SIZE}
        ButtonCmp={CreateButton}
        emptyText="No product announcements found"
        onRowClick={openPreview}
        searchPlaceholder="Search internal name or headline..."
        onSearchChange={(value) => { setSearch(value); setPage(1); }}
        serverPagination={{
          page: pagination.page,
          totalPages: pagination.pages,
          total: pagination.total,
          limit: pagination.limit,
        }}
        onPageChange={setPage}
      />

      {isEditorOpen && (
        <AnnouncementEditor
          title={selectedId ? "Edit product announcement" : "New product announcement"}
          announcement={selectedId ? selectedAnnouncement : null}
          isLoading={Boolean(selectedId) && isSelectedPending}
          loadError={
            isSelectedError
              ? getApiErrorMessage(selectedError, "Could not load the announcement.")
              : ""
          }
          onSave={saveAnnouncement}
          onPublish={publishAnnouncement}
          onNotifyAgain={notifyAgain}
          onUploadMedia={uploadMedia}
          onDeleteMedia={deleteMedia}
          onClose={closeEditor}
          onDirtyChange={setIsEditorDirty}
          isSaving={isSaving}
          isMediaBusy={isMediaBusy}
        />
      )}

      <Modal
        isOpen={isDiscardConfirmOpen}
        onClose={() => setIsDiscardConfirmOpen(false)}
        title="Discard changes"
      >
        <p className="text-sm text-darkergray">
          Discard the unsaved announcement changes? Anything you added since the last save will be lost.
        </p>
        <div className="flex justify-end gap-2 pt-4">
          <Button onClick={() => setIsDiscardConfirmOpen(false)} className="gray-button">Keep editing</Button>
          <Button onClick={discardEditor} className="black-button">Discard</Button>
        </div>
      </Modal>

      <Modal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title="Recipient preview"
        className="max-w-4xl"
      >
        {isSelectedPending ? (
          <div className="py-12 text-center">Loading preview…</div>
        ) : isSelectedError || !selectedAnnouncement ? (
          <div role="alert" className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {getApiErrorMessage(selectedError, "Could not load the preview.")}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-3 rounded-lg bg-lightgray p-4 text-xs text-darkergray sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <span className="block text-darkgray">Created by</span>
                {selectedAnnouncement.createdBy?.name ||
                  selectedAnnouncement.createdBy?.email ||
                  "Admin"}
              </div>
              <div>
                <span className="block text-darkgray">Last edited by</span>
                {selectedAnnouncement.updatedBy?.name ||
                  selectedAnnouncement.updatedBy?.email ||
                  "Admin"}
              </div>
              <div>
                <span className="block text-darkgray">First published</span>
                {selectedAnnouncement.publishedAt
                  ? `${formatAnnouncementDate(
                      selectedAnnouncement.publishedAt,
                    )} by ${
                      selectedAnnouncement.publishedBy?.name ||
                      selectedAnnouncement.publishedBy?.email ||
                      "Admin"
                    }`
                  : "Not published"}
              </div>
              <div>
                <span className="block text-darkgray">Last notified again</span>
                {selectedAnnouncement.lastNotifiedAt
                  ? `${formatAnnouncementDate(
                      selectedAnnouncement.lastNotifiedAt,
                    )} by ${
                      selectedAnnouncement.lastNotifiedBy?.name ||
                      selectedAnnouncement.lastNotifiedBy?.email ||
                      "Admin"
                    }`
                  : "Never"}
              </div>
              <div>
                <span className="block text-darkgray">Archived</span>
                {selectedAnnouncement.archivedAt
                  ? `${formatAnnouncementDate(
                      selectedAnnouncement.archivedAt,
                    )} by ${
                      selectedAnnouncement.archivedBy?.name ||
                      selectedAnnouncement.archivedBy?.email ||
                      "Admin"
                    }`
                  : "Not archived"}
              </div>
            </div>
            <AnnouncementPreview announcement={selectedAnnouncement} />
          </div>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        title={pendingAction?.type === "archive" ? "Archive announcement" : "Delete draft"}
      >
        <p className="text-sm text-darkergray">
          {pendingAction?.type === "archive"
            ? "Archive this published announcement? It will stay in history and cannot be edited."
            : "Delete this draft and its uploaded media? This cannot be undone."}
        </p>
        <div className="flex justify-end gap-2 pt-4">
          <Button onClick={() => setPendingAction(null)} className="gray-button">Cancel</Button>
          <Button onClick={confirmPendingAction} className="black-button" loading={updateMutation.isPending || deleteMutation.isPending}>
            Confirm
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ProductAnnouncements;
